import { MediumIcon } from '@factorialco/deprecated-design-system'
import { BillingAddonReferenceFragment } from 'generated/resources/graphql'

import type { F25AddonDTO, PeriodUnit, VariantsEnum } from 'modules/upsell/lib/selfUpsell/types'
import {
  getValidChargeType,
  getValidCurrency,
  getValidPeriodUnit,
  getValidPricingModel,
  isValidIconMediumType,
  isValidTier,
} from 'modules/upsell/lib/typesValidation'

export const DATO_PLAN_ICON_FIELD = 'iconId'
export const DATO_PLAN_ID_FIELD = 'internalTitle'
export const DATO_PLAN_TITLE_FIELD = 'title'
export const DATO_PLAN_DESCRIPTION_FIELD = 'description'
export const DATO_PLAN_SEG_OPTION_FIELD = 'segmentationOption'
export const BUSINESS_VARIANT = 'Business'
export const ENTERPRISE_VARIANT = 'Enterprise'
export const STARTER_PLAN_TYPE = 'Starter'
export const PRO_PLAN_TYPE = 'Pro'
export const MONTH_PERIOD = 'month'
export const YEAR_PERIOD = 'year'
export const SIX_MONTH_PERIOD = 'six_months'
export const DEFAULT_CURRENCY = 'EUR'
export const DEFAULT_ICON: MediumIcon = 'logo-avatar'

const ISO_COUNTRY_CODES = new Set([
  'us',
  'mx',
  'br',
  'ar',
  'co',
  'es',
  'fr',
  'de',
  'gb',
  'it',
  'pt',
  'nl',
  'se',
  'ch',
  'be',
  'at',
  'cl',
  'bg',
  'hr',
  'cy',
  'cz',
  'ee',
  'gr',
  'hu',
  'lv',
  'lt',
  'mt',
  'pl',
  'ro',
  'sk',
  'si',
  'dk',
  'fi',
  'ie',
  'lu',
  'ea',
  'ic',
  'mo',
  'qa',
  'is',
  'sg',
  'au',
  'hk',
  'sm',
  'ca',
  'il',
  'jp',
  'nz',
  'ae',
  'bs',
  'pr',
  'kr',
  'kw',
])

// https://docs.google.com/spreadsheets/d/1J7mUZbYMJMZqkMhPUHQY5qbeG-VL5lILm_N_nHnWmK8/edit?gid=1079667599#gid=1079667599
const COUNTRY_MAPPING: Record<string, string> = {
  ie: 'de',
  al: 'es',
  bg: 'es',
  hr: 'es',
  cy: 'es',
  cz: 'es',
  ee: 'es',
  gr: 'es',
  hu: 'es',
  lv: 'es',
  lt: 'es',
  mt: 'es',
  pl: 'es',
  ro: 'es',
  sk: 'es',
  si: 'es',
  tr: 'es',
  at: 'de',
  be: 'de',
  dk: 'de',
  fi: 'de',
  lu: 'de',
  nl: 'de',
  se: 'de',
  ch: 'de',
  mc: 'de',
  no: 'de',
}

export function getVariantFromID(id: string): VariantsEnum {
  if (id.includes('Enterprise')) return ENTERPRISE_VARIANT
  return BUSINESS_VARIANT
}

const periodMap: Record<string, PeriodUnit> = {
  month: 'month',
  year: 'year',
  '6_m': 'six_months',
}

export const getCurrentPeriod = (plan: string): PeriodUnit | undefined => {
  const key = Object.keys(periodMap).find((pattern) => plan.includes(pattern))
  return key ? periodMap[key] : undefined
}

export const mapToF25Addon = (addons: BillingAddonReferenceFragment[]): F25AddonDTO[] => {
  return addons.map((addon) => ({
    internalTitle: addon.internalTitle,
    title: addon.title,
    description: addon.description,
    segmentationOption: undefined,
    minimumSeats: undefined,
    iconId: isValidIconMediumType(addon.iconId) ? addon.iconId : DEFAULT_ICON,
    regions: addon.regions.map((region) => ({
      chargebeeAddons:
        region.chargebeeAddons.map((add) => ({
          addonId: add.addonId,
          chargeType: getValidChargeType(add.chargeType),
          currencyCode: getValidCurrency(add.currencyCode),
          period: add.period,
          periodUnit: getValidPeriodUnit(add.periodUnit),
          pricingModel: getValidPricingModel(add.pricingModel),
          price: add.price || null,
          selectedCountries: add.selectedCountries || undefined,
          tiers:
            add.tiers
              ?.map((tier) => ({
                price: tier.price,
                startingUnit: tier.startingUnit,
                endingUnit: tier.endingUnit || undefined,
              }))
              .filter(isValidTier) || [],
        })) || [],
      stairStepUsageLimits: [],
      perUnitUsageLimits: [],
      features: region.features.map((feature) => ({
        title: feature.title,
        featureStatus: feature.featureStatus || undefined,
        tooltip: feature.tooltip || undefined,
      })),
    })),
  }))
}

export const formatDate = (date: Date) => {
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export const getCountryFromPlan = (plan: string, defaultCountry: string): string => {
  const match = plan.match(/-([a-z]{2})-/i)
  const code = match && match[1] ? match[1].toLowerCase() : defaultCountry.toLowerCase()

  const mappedCode = COUNTRY_MAPPING[code] || code

  return ISO_COUNTRY_CODES.has(mappedCode) ? mappedCode : defaultCountry.toLowerCase()
}
